import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Pencil, SlidersHorizontal } from "lucide-react";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";

import { api } from "../api/client";
import type { DronePlan, DronesParams, DronesPreviewRequest, DroneRegime, MtoTarget, NamedPlace, Phase, WeatherCustom, ZoneLetter } from "../api/types";
import { attentionSelector, designate } from "../components/attention";
import { Callout } from "../components/Callout";
import { Checkbox } from "../components/Checkbox";
import { Spinner } from "../components/EmptyState";
import { DisclosureRow } from "../components/panel";
import { Motion, Presence } from "../components/presence";
import { Select } from "../components/Select";
import { Badge, Button, inputClass, Segmented } from "../components/ui";
import { Waiting } from "../components/Waiting";
import { assetLabel, capabilityLine, capabilitySource, MTO_TARGETS, mtoLabel, REGIMES, regimeLabel, regimeRank, smallDronesEffect, VAA_STEPS } from "../drones";
import { formatNumber, k, locale, t } from "../i18n/i18n";
import { missionName } from "../i18n/vocabulary";
import { useUi } from "../state/ui";
import { DRONES_TARGETS, OPEN_DRONES_SETTINGS } from "./missionDataTarget";
import { dronesParamsOf } from "./missionDraft";
import { useWeatherOptions } from "./WeatherField";

/** Section « Drones » des Données initiales (chantier « Drones », D2 à D8 ; amendement A1/A2). D'abord un résumé de
 *  deux à trois lignes — moyens déduits de l'ordre de bataille, régime par temps en une phrase, capacité drone de
 *  l'ENI, état des petits drones s'il les gêne —, puis les réglages repliés sous « Régler l'emploi des drones » :
 *  régime T1/T2/T3, plafond du VAA, priorités des munitions téléopérées, bulle de brouillage, caméra thermique.
 *  Tout est déduit par le serveur (`POST /drones/preview`, le brouillon tel qu'il est) : un utilisateur qui ne touche
 *  à rien obtient des ordres complets ; chaque valeur déduite dit d'où elle vient en une phrase, chaque retouche
 *  revient au déduit en un clic. Le dépliage s'ouvre de lui-même quand un réglage est retouché ou quand une autre vue
 *  désigne un réglage (`OPEN_DRONES_SETTINGS`). */

const PHASES: readonly Phase[] = ["T1", "T2", "T3"];

type Notify = ReturnType<typeof useUi.getState>["notify"];

/** Cibles `data-attention` des réglages, désignables par `OPEN_DRONES_SETTINGS` (`detail.target`). */
export { DRONES_TARGETS };

// --- Ouverture demandée par une autre vue --------------------------------------------------------------------------

/** Demande d'ouverture reçue avant le montage de la section (la vue s'ouvre juste après l'événement) : gardée le
 *  temps que la section la lise. */
let pendingOpen: { at: number; target: string | null } | null = null;
const openListeners = new Set<() => void>();
const OPEN_TTL_MS = 3000;

if (typeof window !== "undefined")
  window.addEventListener(OPEN_DRONES_SETTINGS, (event) => {
    const target = (event as CustomEvent<{ target?: string } | undefined>).detail?.target;
    pendingOpen = { at: Date.now(), target: typeof target === "string" ? target : null };
    openListeners.forEach((listener) => listener());
  });

/** Demande d'ouverture encore fraîche, consommée par la section qui la lit. */
function takeOpenRequest(): { target: string | null } | null {
  const request = pendingOpen;
  pendingOpen = null;
  return request && Date.now() - request.at <= OPEN_TTL_MS ? request : null;
}

// --- Réglages -------------------------------------------------------------------------------------------------------

/** Réglages nettoyés : sous-clés vides retirées ; plus rien : null (tout est déduit, comme le serveur l'enregistre). */
export const cleanParams = dronesParamsOf;

/** Un réglage au moins est retouché (le dépliage s'ouvre de lui-même). */
export function hasChosen(params: DronesParams | null | undefined): boolean {
  return cleanParams(params) !== null;
}

// --- Phrases ---------------------------------------------------------------------------------------------------------

function listOf(items: string[]): string {
  try {
    return new Intl.ListFormat(locale(), { type: "conjunction" }).format(items);
  } catch {
    return items.join(", ");
  }
}

const REGIME_PART: Record<DroneRegime, string> = {
  libre: k("libres en {phases}"),
  sur_ordre: k("sur ordre en {phases}"),
  interdit: k("interdits en {phases}"),
};
const REGIME_ALL: Record<DroneRegime, string> = {
  libre: k("Vols libres à tous les temps."),
  sur_ordre: k("Vols sur ordre à tous les temps."),
  interdit: k("Vols interdits à tous les temps."),
};

/** Régime en une phrase : « Vols libres en T1 et T3, sur ordre en T2. ». */
export function regimeSentence(regime: Record<Phase, DroneRegime>): string {
  // Dans l'ordre des temps : le régime du premier temps d'abord (« Vols interdits en T1, sur ordre en T2 et T3. »).
  const groups = REGIMES.map((r) => ({ regime: r, phases: PHASES.filter((p) => regime[p] === r) }))
    .filter((g) => g.phases.length)
    .sort((a, b) => PHASES.indexOf(a.phases[0]!) - PHASES.indexOf(b.phases[0]!));
  if (groups.length === 1) return t(REGIME_ALL[groups[0]!.regime]);
  const parts = groups.map((g) => t(REGIME_PART[g.regime], { phases: listOf([...g.phases]) }));
  return t("Vols {parts}.", { parts: parts.join(", ") });
}

const POSTURE_REASON: Record<string, string> = {
  offensive: k("Déduit de la posture offensive."),
  defensive: k("Déduit de la posture défensive."),
  securisation: k("Déduit de la posture de sécurisation."),
  assistance: k("Déduit de la posture d'assistance."),
  default: k("Déduit d'une posture offensive, faute de mission."),
};

/** D'où vient le régime déduit, en une phrase (« Déduit de la mission « S'infiltrer ». ») ; passage de l'ALAT
 *  compris (division). */
export function regimeReason(plan: Pick<DronePlan, "regime_reason" | "alat_phases">): string {
  const reason = plan.regime_reason.startsWith("mission:")
    ? t("Déduit de la mission « {mission} ».", { mission: missionName(plan.regime_reason.slice(8)) })
    : t(POSTURE_REASON[plan.regime_reason] ?? POSTURE_REASON.default!);
  return plan.alat_phases.length
    ? t("{reason} Sur ordre au moins en {phases}, au passage de l'ALAT.", { reason, phases: listOf(plan.alat_phases) })
    : reason;
}

/** Moyens du plan en une ligne, par nature : « Drones de contact × 4 · Micro-drones × 10 ». */
export function assetsLine(plan: Pick<DronePlan, "assets">): string {
  const counts = new Map<string, number>();
  for (const asset of plan.assets) counts.set(asset.kind, (counts.get(asset.kind) ?? 0) + asset.count);
  return [...counts.entries()].map(([kind, n]) => `${assetLabel(kind as DronePlan["assets"][number]["kind"])} × ${formatNumber(n)}`).join(" · ");
}

// --- Petits composants -----------------------------------------------------------------------------------------------

/** Ligne du résumé : texte et, à droite, sa pastille ou son lien. */
function SummaryLine({ children, tone = "soft", trailing }: { children: ReactNode; tone?: "soft" | "warn"; trailing?: ReactNode }) {
  return (
    <p className={tone === "warn" ? "text-ui leading-snug text-warn" : "text-ui leading-snug text-ink-soft"}>
      {children}
      {trailing && <span className="ml-1.5 inline-flex align-middle">{trailing}</span>}
    </p>
  );
}

/** Intitulé d'un réglage, avec sa pastille (« choisi », « Cadre du chef », « déduit »). */
function SettingLabel({ children, badge }: { children: ReactNode; badge?: ReactNode }) {
  return (
    <span className="mb-1 flex flex-wrap items-center gap-1.5 text-ui font-medium text-ink-soft">
      {children}
      {badge}
    </span>
  );
}

/** Phrase de déduction d'un réglage et, s'il est retouché, « Revenir au déduit » (en fondu). */
function Deduced({ text, chosen, revertLabel, onRevert }: { text: string; chosen: boolean; revertLabel?: string; onRevert: () => void }) {
  return (
    <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-meta leading-snug text-ink-muted">
      <span>{text}</span>
      <Presence show={chosen}>
        <Motion origin="fade" className="inline-flex">
          <Button variant="link" onClick={onRevert}>
            {revertLabel ?? t("Revenir au déduit")}
          </Button>
        </Motion>
      </Presence>
    </div>
  );
}

const chosenBadge = () => (
  <Badge size="sm" tone="accent">
    {t("choisi")}
  </Badge>
);

function useDebounced<T>(value: T, delay: number): T {
  const [shown, setShown] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setShown(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return shown;
}

// --- Section ---------------------------------------------------------------------------------------------------------

export interface DronesDraft {
  level: DronesPreviewRequest["level"];
  odb: string | null;
  mission: string | null;
  mode_action: string | null;
  posture?: DronesPreviewRequest["posture"];
  enemy_type: string | null;
  custom_enemy?: string | null;
  eni_odb?: string | null;
  drones?: DronesParams | null;
}

export function DronesField({
  exerciseId,
  draft,
  weather,
  onChange,
  onEditCustomEnemy,
  readOnly,
  conflict,
}: {
  exerciseId: string | null;
  draft: DronesDraft;
  /** Météo du brouillon (même requête que le réglage « Météo ») : état des petits drones du résumé. */
  weather: {
    mapSlug: string | null;
    /** Lieu nommé du nouveau terrain : la ligne des petits drones suit son climat estimé. */
    place?: NamedPlace | null;
    startDtg: string | null;
    timeZone: ZoneLetter | null;
    scenario: string | null;
    custom: WeatherCustom | null | undefined;
    waiting: boolean;
  };
  onChange: (params: DronesParams | null) => void;
  /** « Modifier la fiche » de l'ENI personnalisé choisi. */
  onEditCustomEnemy?: (id: string) => void;
  /** Dossier en lecture seule : chaque commande désigne les droits. */
  readOnly?: () => void;
  conflict?: ReactNode;
}) {
  // Avis lu au moment de l'appui (jamais l'action figée au rendu).
  const notify: Notify = (notice) => useUi.getState().notify(notice);
  const params = draft.drones ?? null;
  const body: DronesPreviewRequest = useMemo(
    () => ({
      exercise_id: exerciseId,
      level: draft.level,
      odb: draft.odb,
      mission: draft.mission,
      mode_action: draft.mode_action,
      posture: draft.posture ?? null,
      enemy_type: draft.enemy_type,
      custom_enemy: draft.custom_enemy ?? null,
      eni_odb: draft.eni_odb ?? null,
      drones: cleanParams(params),
    }),
    [exerciseId, draft.level, draft.odb, draft.mission, draft.mode_action, draft.posture, draft.enemy_type, draft.custom_enemy, draft.eni_odb, params],
  );
  const debounced = useDebounced(body, 300);
  const query = useQuery({
    queryKey: ["drones-preview", debounced],
    queryFn: () => api.dronesPreview(debounced),
    // Sans niveau ni ordre de bataille, rien à déduire : la phrase désigne le choix attendu.
    enabled: Boolean(debounced.level && debounced.odb),
    placeholderData: keepPreviousData,
    retry: false,
  });
  const plan = query.data?.available ? query.data.plan : null;
  const { options } = useWeatherOptions({ ...weather });
  const smallLine = smallDronesEffect(options?.preview?.small_drones, options?.ephemeris?.darkness);

  // Dépliage : ouvert d'office quand un réglage est retouché, ou sur demande d'une autre vue (cohérence).
  const [open, setOpen] = useState(() => hasChosen(params));
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (hasChosen(params)) setOpen(true);
  }, [params]);
  useEffect(() => {
    const serve = () => {
      const request = takeOpenRequest();
      if (!request) return;
      setOpen(true);
      // Après l'ouverture du dépliage (`--gx-enter`) : le réglage demandé vient à l'écran et est désigné.
      if (request.target)
        window.setTimeout(() => designate(ref.current?.querySelector<HTMLElement>(attentionSelector(request.target!)) ?? null, "ring"), 280);
    };
    serve();
    openListeners.add(serve);
    return () => {
      openListeners.delete(serve);
    };
  }, []);

  const write = (next: DronesParams | null) => (readOnly ? readOnly() : onChange(cleanParams(next)));
  const patch = (changes: Partial<DronesParams>) => write({ ...(params ?? {}), ...changes });

  const designateOdb = () => {
    designate(`${attentionSelector("dossier-odb")} [role="combobox"]`, "field");
    notify({ text: t("Choisissez l'ordre de bataille de la force."), tone: "info" });
  };

  const noOdb = !draft.odb || query.data?.reason === "no_odb";
  return (
    <div ref={ref} data-attention="dossier-drones" className="space-y-1 rounded-lg">
      {noOdb ? (
        <SummaryLine>
          <Button variant="link" onClick={designateOdb}>
            {t("Choisissez l'ordre de bataille pour déduire les moyens drones.")}
          </Button>
        </SummaryLine>
      ) : !plan ? (
        query.isError ? (
          <SummaryLine tone="warn">{t("Emploi des drones indisponible : {reason}", { reason: query.error.message })}</SummaryLine>
        ) : (
          <p className="flex items-center gap-2 text-ui text-ink-muted">
            <Spinner decorative /> <Waiting>{t("Calcul de l'emploi des drones…")}</Waiting>
          </p>
        )
      ) : (
        <Motion origin="fade" className="space-y-1">
          <Summary plan={plan} smallLine={smallLine} onEditCustomEnemy={draft.custom_enemy ? () => onEditCustomEnemy?.(draft.custom_enemy!) : undefined} />
          {conflict}
          <DisclosureRow icon={SlidersHorizontal} title={t("Régler l'emploi des drones")} open={open} onToggle={() => setOpen(!open)}>
            <Settings plan={plan} params={params} patch={patch} notify={notify} />
          </DisclosureRow>
        </Motion>
      )}
    </div>
  );
}

function Summary({ plan, smallLine, onEditCustomEnemy }: { plan: DronePlan; smallLine: string | null; onEditCustomEnemy?: () => void }) {
  const enemy = capabilityLine(plan.enemy);
  return (
    <div className="space-y-1">
      {plan.assets_source === "none" ? (
        <SummaryLine>{t("Aucun moyen drone dans l'ordre de bataille.")}</SummaryLine>
      ) : (
        <SummaryLine
          trailing={
            <Badge size="sm" tone={plan.assets_source === "odb" ? "friend" : "neutral"}>
              {plan.assets_source === "odb" ? t("D'après l'ordre de bataille") : t("Valeurs doctrinales")}
            </Badge>
          }
        >
          {assetsLine(plan)}
        </SummaryLine>
      )}
      <SummaryLine>{regimeSentence(plan.regime)}</SummaryLine>
      {enemy && plan.enemy && (
        <SummaryLine
          trailing={
            plan.enemy.source === "custom" && onEditCustomEnemy ? (
              <Button variant="link" onClick={onEditCustomEnemy}>
                <Pencil size={14} /> {t("Modifier la fiche")}
              </Button>
            ) : undefined
          }
        >
          {t("{line} ({source})", { line: enemy, source: capabilitySource(plan.enemy) })}
        </SummaryLine>
      )}
      <Presence show={Boolean(smallLine)}>
        {smallLine && (
          <Motion origin="fade">
            <SummaryLine tone="warn">{smallLine}</SummaryLine>
          </Motion>
        )}
      </Presence>
    </div>
  );
}

const PRIORITY_LABELS = [k("Priorité 1"), k("Priorité 2"), k("Priorité 3")];
const JAMMING_NONE = "none";
type JammingValue = Phase | typeof JAMMING_NONE;

function Settings({
  plan,
  params,
  patch,
  notify,
}: {
  plan: DronePlan;
  params: DronesParams | null;
  patch: (changes: Partial<DronesParams>) => void;
  notify: Notify;
}) {
  const chiefRegime = plan.chief?.regime ?? {};
  const chiefCeiling = plan.chief?.vaa_ceiling_m ?? null;
  const regimeChosen = PHASES.some((p) => plan.regime_source[p] === "chosen");
  const setRegime = (phase: Phase, value: DroneRegime) => {
    const floor = chiefRegime[phase];
    if (floor && regimeRank(value) < regimeRank(floor)) {
      notify({ text: t("Le chef impose au moins « {regime} » en {phase}.", { regime: regimeLabel(floor), phase }), tone: "info" });
      return;
    }
    if (value === plan.regime[phase] && plan.regime_source[phase] !== "chosen") return;
    patch({ regime: { ...(params?.regime ?? {}), [phase]: value } });
  };
  const priorities = plan.mto.priorities;
  const setPriority = (index: number, value: MtoTarget) => {
    const next = [...priorities];
    next[index] = value;
    patch({ mto_priorities: next.slice(0, 3) });
  };
  const jamming = plan.jamming_phase;
  const flying = jamming && plan.regime[jamming] !== "interdit" ? jamming : null;
  return (
    <div className="space-y-4 px-2 pt-2 pb-3">
      {/* Régime par temps : libre, sur ordre, interdit ; le cadre du chef estompe les options plus libres. */}
      <div data-attention={DRONES_TARGETS.regime} className="rounded-lg">
        <SettingLabel>{t("Régime d'emploi")}</SettingLabel>
        <div className="space-y-1.5">
          {PHASES.map((phase) => {
            const floor = chiefRegime[phase];
            return (
              <div key={phase} className="grid grid-cols-[2rem_1fr] items-center gap-2">
                <span className="text-ui font-medium text-ink-soft">{phase}</span>
                <div className="flex min-w-0 items-center gap-1.5">
                  <Segmented
                    ariaLabel={t("Régime en {phase}", { phase })}
                    size="sm"
                    className="min-w-0 flex-1"
                    options={REGIMES.map((r) => ({ value: r, label: regimeLabel(r), blocked: Boolean(floor && regimeRank(r) < regimeRank(floor)) }))}
                    value={plan.regime[phase]}
                    onChange={(value) => setRegime(phase, value)}
                  />
                  {floor ? (
                    <Badge size="sm" tone="neutral">
                      {t("Cadre du chef")}
                    </Badge>
                  ) : plan.regime_source[phase] === "chosen" ? (
                    chosenBadge()
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
        <p className="mt-1.5 text-meta leading-snug text-ink-muted">
          {t("Libre : chaque chef emploie ses drones dans son VAA sans demande. Sur ordre : chaque vol est demandé au PC.")}
        </p>
        <Deduced text={regimeReason(plan)} chosen={regimeChosen} revertLabel={t("Revenir au régime déduit")} onRevert={() => patch({ regime: null })} />
      </div>

      {/* Volume aérien alloué : du sol au plafond, au plus celui du chef. */}
      <div data-attention={DRONES_TARGETS.vaa} className="rounded-lg">
        <SettingLabel badge={plan.vaa.source === "chosen" ? chosenBadge() : plan.vaa.source === "chief" ? <Badge size="sm">{t("Cadre du chef")}</Badge> : undefined}>
          {t("Plafond du VAA")}
        </SettingLabel>
        <Select
          className={inputClass}
          aria-label={t("Plafond du VAA")}
          value={String(plan.vaa.ceiling_m)}
          onChange={(e) => patch({ vaa_ceiling_m: Number(e.target.value) })}
        >
          {VAA_STEPS.map((m) => (
            <option key={m} value={String(m)} disabled={chiefCeiling !== null && m > chiefCeiling}>
              {t("{m} m sol", { m: formatNumber(m) })}
            </option>
          ))}
        </Select>
        <Deduced
          text={
            chiefCeiling !== null
              ? t("Du sol au plafond, au plus {m} m (cadre du chef) ; désactivé au passage de l'ALAT.", { m: formatNumber(chiefCeiling) })
              : t("Déduit du niveau : du sol au plafond ; désactivé au passage de l'ALAT.")
          }
          chosen={plan.vaa.source === "chosen"}
          onRevert={() => patch({ vaa_ceiling_m: null })}
        />
      </div>

      {/* Priorités des munitions téléopérées : une cible ne se choisit qu'une fois. */}
      <div data-attention={DRONES_TARGETS.mto} className="rounded-lg">
        <SettingLabel badge={plan.mto.source === "chosen" ? chosenBadge() : undefined}>{t("Priorités des munitions téléopérées")}</SettingLabel>
        <div className="space-y-1.5">
          {[0, 1, 2].map((index) => (
            <div key={index} className="grid grid-cols-[2rem_1fr] items-center gap-2">
              <span className="text-ui font-medium text-ink-soft tabular-nums">{index + 1}</span>
              <Select
                className={inputClass}
                aria-label={t(PRIORITY_LABELS[index]!)}
                value={priorities[index] ?? ""}
                onChange={(e) => setPriority(index, e.target.value as MtoTarget)}
              >
                {MTO_TARGETS.map((target) => (
                  <option key={target.key} value={target.key} disabled={priorities.includes(target.key) && priorities[index] !== target.key}>
                    {mtoLabel(target.key)}
                  </option>
                ))}
              </Select>
            </div>
          ))}
        </div>
        <Deduced
          text={plan.mto.available ? t("Déduites de la posture.") : t("Aucune munition téléopérée dans l'ordre de bataille : priorités des munitions reçues.")}
          chosen={plan.mto.source === "chosen"}
          onRevert={() => patch({ mto_priorities: null })}
        />
      </div>

      {/* Bulle de brouillage : nos vols y sont suspendus ; un vol prévu pendant la bulle se corrige en un clic. */}
      <div data-attention={DRONES_TARGETS.jamming} className="rounded-lg">
        <Segmented<JammingValue>
          label={t("Bulle de brouillage")}
          size="sm"
          options={[{ value: JAMMING_NONE, label: t("Aucune") }, ...PHASES.map((p) => ({ value: p, label: p }))]}
          value={jamming ?? JAMMING_NONE}
          onChange={(value) => patch({ jamming_phase: value === JAMMING_NONE ? null : value })}
        />
        <p className="mt-1 text-meta leading-snug text-ink-muted">{t("Prive aussi nos drones de liaison : nos vols y sont suspendus.")}</p>
        <Presence show={Boolean(flying)}>
          {flying && (
            <Motion origin="fade" className="mt-2">
              <Callout
                tone="warn"
                action={
                  <Button variant="secondary" size="sm" onClick={() => setRegime(flying, "interdit")}>
                    {t("Interdire les vols en {phase}", { phase: flying })}
                  </Button>
                }
              >
                {t("Nos drones volent en {phase} pendant la bulle de brouillage.", { phase: flying })}
              </Callout>
            </Motion>
          )}
        </Presence>
      </div>

      {/* Caméra thermique : déduite des moyens (micro, contact et tactiques en ont, les munitions téléopérées non). */}
      <div data-attention={DRONES_TARGETS.thermal} className="rounded-lg">
        <div className="flex flex-wrap items-center gap-1.5">
          <Checkbox checked={plan.thermal} onChange={(checked) => patch({ thermal: checked })} label={t("Nos drones ont une caméra thermique")} />
          {plan.thermal_source === "deduced" ? (
            <Badge size="sm" tone="neutral">
              {t("déduit")}
            </Badge>
          ) : (
            chosenBadge()
          )}
        </div>
        <Deduced
          text={t("Déduite des moyens : les micro-drones, drones de contact et drones tactiques en ont, les munitions téléopérées non.")}
          chosen={plan.thermal_source === "chosen"}
          onRevert={() => patch({ thermal: null })}
        />
      </div>

    </div>
  );
}
